import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, MoreVertical, RefreshCw, Edit, Trash2, Save, X, Users, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useGetBoard, useListBoardWidgets, useUpdateBoard, useResyncWidget, useListBoardAccess } from "@/hooks/useBoard"
import { useToast } from "@/hooks/useToast"
import { getMemberAvatarGradient } from "@/lib/utils/avatar"
import { cn } from "@/lib/utils"
import { ROUTES } from "@/routes/path"
import { Widget } from "./components/widget"
import { ShareBoardDialog } from "./components/share-board-dialog"
import { EditBoardDialog } from "./components/edit-board-dialog"
import { DeleteBoardDialog } from "./components/delete-board-dialog"
import Loader from "@/components/ui/loader"
import GridLayout, { Layout } from "react-grid-layout"
import type { LayoutItem } from "react-grid-layout"
import "react-grid-layout/css/styles.css"

function getInitials(firstName: string, lastName: string, email: string) {
  if (firstName || lastName) {
    return [firstName, lastName]
      .filter(Boolean)
      .map((s) => s.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }
  if (email) return email.slice(0, 2).toUpperCase()
  return "?"
}

// Prevent text selection during drag/resize
const style = document.createElement("style")
style.textContent = `
  .react-grid-item.react-draggable-dragging,
  .react-grid-item.react-resizable-resizing,
  .react-grid-item.react-draggable-dragging *,
  .react-grid-item.react-resizable-resizing * {
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
  }
`
if (!document.head.querySelector("style[data-grid-layout-no-select]")) {
  style.setAttribute("data-grid-layout-no-select", "true")
  document.head.appendChild(style)
}

export default function BoardDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: board, isLoading: boardLoading } = useGetBoard(id || null)
  const { data: widgets, isLoading: widgetsLoading } = useListBoardWidgets(id || null)
  const updateBoard = useUpdateBoard()
  const resyncAll = useResyncWidget()
  const { data: boardAccess } = useListBoardAccess(id || null)

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [layout, setLayout] = useState<LayoutItem[]>([])
  const [originalLayout, setOriginalLayout] = useState<LayoutItem[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const isInitializing = useRef(true)

  // Initialize layout from board config or create default layout
  useEffect(() => {
    if (widgets && widgets.length > 0) {
      isInitializing.current = true
      const newLayout: LayoutItem[] = widgets.map((widget, index) => {
        // Try to get saved layout from config
        const savedLayout = board?.config?.layout?.find((item: LayoutItem) => item.i === widget.id)
        if (savedLayout) {
          return {
            i: widget.id,
            x: savedLayout.x ?? (index % 4) * 3,
            y: savedLayout.y ?? Math.floor(index / 4) * 3,
            w: savedLayout.w ?? 3,
            h: savedLayout.h ?? 3,
          }
        }
        // Default layout: 3x3 grid units, arranged in rows of 4
        return {
          i: widget.id,
          x: (index % 4) * 3,
          y: Math.floor(index / 4) * 3,
          w: 3,
          h: 3,
        }
      })
      const normalizedLayout = newLayout.map((item) => ({
        i: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
      }))
      setLayout(normalizedLayout as LayoutItem[])
      setOriginalLayout(JSON.parse(JSON.stringify(normalizedLayout)) as LayoutItem[])
      // Reset initialization flag after a brief delay to allow GridLayout to settle
      setTimeout(() => {
        isInitializing.current = false
      }, 100)
    } else {
      setLayout([])
      setOriginalLayout([])
      isInitializing.current = false
    }
  }, [board, widgets])

  // Check for unsaved changes (only after initialization is complete)
  useEffect(() => {
    if (isInitializing.current) {
      return
    }

    // Normalize layouts for comparison (only compare essential properties)
    const normalizeLayout = (items: LayoutItem[]) =>
      items
        .map((item) => ({
          i: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        }))
        .sort((a, b) => a.i.localeCompare(b.i))

    const normalizedLayout = normalizeLayout(layout)
    const normalizedOriginal = normalizeLayout(originalLayout)

    const hasChanges = JSON.stringify(normalizedLayout) !== JSON.stringify(normalizedOriginal)
    setHasUnsavedChanges(hasChanges)
  }, [layout, originalLayout])

  const handleLayoutChange = useCallback((newLayout: Layout) => {
    // Ignore layout changes during initialization
    if (isInitializing.current) {
      return
    }

    // Normalize the layout to only include essential properties
    const normalized = newLayout.map((item) => ({
      i: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    }))
    setLayout(normalized as LayoutItem[])
  }, [])

  const handleSave = async () => {
    if (!id || !board) return

    try {
      const config = {
        layout: layout,
      }

      await updateBoard.mutateAsync({ id, config })
      setOriginalLayout(JSON.parse(JSON.stringify(layout)))
      setHasUnsavedChanges(false)
      toast({
        description: "Board layout saved successfully",
      })
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to save board layout",
        variant: "destructive",
      })
    }
  }

  const handleCancel = () => {
    setLayout(JSON.parse(JSON.stringify(originalLayout)))
    setHasUnsavedChanges(false)
  }

  const handleResyncAll = async () => {
    if (!id || !widgets) return

    try {
      await Promise.all(widgets.map((widget) => resyncAll.mutateAsync({ boardId: id, widgetId: widget.id })))
      toast({
        description: "All widgets refreshed successfully",
      })
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to refresh widgets",
        variant: "destructive",
      })
    }
  }

  if (boardLoading) {
    return (
      <div className="mt-8 flex h-full items-center justify-center">
        <Loader />
      </div>
    )
  }

  if (!board) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-gray-600 dark:text-gray-400">Board not found</p>
          <Button onClick={() => navigate(ROUTES.DASHBOARD.BOARDS.LIST)}>Back to Boards</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Compact Header */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-2.5 dark:border-gray-800 dark:bg-[#0F0F12]">
        <div className="mx-auto flex max-w-[1920px] items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(ROUTES.DASHBOARD.BOARDS.LIST)}
              className="h-7 w-7"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>
            <div className="flex items-center gap-2.5">
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">{board?.name}</h1>
              {board?.description && (
                <span className="text-xs text-gray-500 dark:text-gray-400">· {board.description}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate(ROUTES.DASHBOARD.EDITOR.QUERY_EDITOR)}
              className="h-7 gap-1.5 px-2.5 text-xs"
            >
              <Plus className="h-3 w-3" />
              Create Widget
            </Button>
            {(boardAccess?.members ?? []).length > 0 || (boardAccess?.role_grants ?? []).length > 0 ? (
              <TooltipProvider delayDuration={300}>
                <div className="flex -space-x-2.5">
                  {(boardAccess?.members ?? []).map((m) => {
                    const displayName = m.first_name || m.last_name ? `${m.first_name} ${m.last_name}`.trim() : m.email
                    return (
                      <Tooltip key={m.account_id}>
                        <TooltipTrigger asChild>
                          <Avatar className="h-7 w-7 cursor-default border-2 border-white dark:border-gray-900">
                            <AvatarFallback
                              className={cn("text-xs font-medium text-white", getMemberAvatarGradient(m.account_id))}
                            >
                              {getInitials(m.first_name, m.last_name, m.email)}
                            </AvatarFallback>
                          </Avatar>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {displayName}
                          <TooltipArrow />
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
                  {(boardAccess?.role_grants ?? []).map((g) => (
                    <Tooltip key={g.role_id}>
                      <TooltipTrigger asChild>
                        <Avatar className="h-7 w-7 cursor-default border-2 border-white dark:border-gray-900">
                          <AvatarImage
                            src={`https://avatar.vercel.sh/${encodeURIComponent(g.role_name)}?size=32`}
                            alt=""
                          />
                          <AvatarFallback className="bg-gray-100 text-xs font-medium text-gray-600 dark:bg-gray-600 dark:text-gray-300">
                            {g.role_name?.charAt(0)?.toUpperCase() ?? "?"}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {g.role_name}
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  {board?.is_owner && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsShareOpen(true)
                      }}
                      className="h-7 w-7 shrink-0 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600"
                      title="Add people or roles"
                    >
                      <Plus className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                    </Button>
                  )}
                </div>
              </TooltipProvider>
            ) : board?.is_owner ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsShareOpen(true)}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                <Users className="h-3.5 w-3.5" />
                Share
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleResyncAll} disabled={resyncAll.isPending} className="text-xs">
                  <RefreshCw className={`mr-2 h-3.5 w-3.5 ${resyncAll.isPending ? "animate-spin" : ""}`} />
                  Refresh All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsEditOpen(true)} className="text-xs">
                  <Edit className="mr-2 h-3.5 w-3.5" />
                  Edit Board
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsDeleteOpen(true)}
                  className="text-xs text-red-600 dark:text-red-400"
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                  Delete Board
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Widgets Grid */}
      <div className="min-h-0 flex-1 overflow-auto">
        {widgetsLoading ? (
          <div className="mt-8 flex h-full items-center justify-center">
            <Loader />
          </div>
        ) : !widgets || widgets.length === 0 ? (
          <div className="mt-16 flex h-full items-center justify-center">
            <div className="text-center">
              <p className="mb-1 text-sm font-medium text-gray-600 dark:text-gray-400">No widgets yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Export outputs from the query editor to add widgets to this board
              </p>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-full p-3" style={{ userSelect: "none" }}>
            <GridLayout
              className="layout"
              layout={layout as unknown as Layout}
              width={window.innerWidth - 30}
              gridConfig={{
                cols: 12,
                rowHeight: 80,
                margin: [12, 12],
                containerPadding: [0, 0],
              }}
              dragConfig={{
                enabled: true,
                handle: ".drag-handle",
              }}
              resizeConfig={{
                enabled: true,
              }}
              onLayoutChange={handleLayoutChange}
            >
              {widgets.map((widget) => (
                <div key={widget.id}>
                  <Widget
                    widget={widget}
                    boardId={id!}
                    onDelete={() => {
                      // Widget deletion is handled by the widget component itself
                    }}
                  />
                </div>
              ))}
            </GridLayout>
          </div>
        )}
      </div>

      <EditBoardDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        boardId={id!}
        initialName={board?.name ?? ""}
        initialDescription={board?.description ?? ""}
      />
      <DeleteBoardDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        boardId={id!}
        onSuccess={() => navigate(ROUTES.DASHBOARD.BOARDS.LIST)}
      />
      <ShareBoardDialog open={isShareOpen} onOpenChange={setIsShareOpen} boardId={id!} />

      {/* Floating Save/Cancel Buttons */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 transform">
          <div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Unsaved changes</span>
            <div className="h-4 w-px bg-gray-200 dark:bg-gray-700" />
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={handleCancel} className="h-7 gap-1.5 px-2.5 text-xs">
                <X className="h-3 w-3" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={updateBoard.isPending}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                <Save className="h-3 w-3" />
                {updateBoard.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
